import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  listPaymentAccounts, createPaymentAccount, updatePaymentAccount, deletePaymentAccount, assignPaymentAccount, setConsultPayGym,
} from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CreditCard, Plus, RefreshCw, Trash2 } from "lucide-react";

const KEY_LABELS = { app_id: "App ID", secret_key: "Secret key" };
const DESK = "";   // select value for "pay at the front desk"
const selectCls = "h-9 rounded-md border border-[#262626] bg-[#0b0b0b] px-2 text-sm text-zinc-100";

export default function PaymentAccounts() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);   // account, or {} for a new one

  const load = useCallback(async () => {
    try {
      setData(await listPaymentAccounts());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load payment accounts");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (user?.role !== "master_admin") return <div className="card-surface p-8 text-sm text-zinc-500">Master Admins only.</div>;

  const assign = async (gym, accountId) => {
    try {
      await assignPaymentAccount([gym.id], accountId);
      toast.success(accountId ? `${gym.name} now takes payments through ${data.accounts.find((a) => a.id === accountId)?.label}` : `${gym.name}: renewals at the front desk`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change the gym's account");
    }
  };

  const remove = async (acc) => {
    if (!window.confirm(`Delete ${acc.label}? Its keys are deleted too.`)) return;
    try {
      await deletePaymentAccount(acc.id);
      toast.success(`${acc.label} deleted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const accountName = (id) => data?.accounts.find((a) => a.id === id);
  return (
    <div className="space-y-6" data-testid="payment-accounts-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Admin · Member app</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Payments</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
            Members renew in the app and pay through the gateway account their gym is on. Several gyms can share one
            account, and any gym can move to its own later. Gyms with no account take renewal requests in the app and
            the front desk collects payment. Paid renewals go straight into All Sales.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setEditing({})} data-testid="payment-add"><Plus className="mr-1.5 h-4 w-4" /> Add account</Button>
          <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {data?.accounts.map((a) => (
          <div key={a.id} className="card-surface p-4" data-testid={`payment-account-${a.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2 font-semibold text-zinc-100"><CreditCard className="h-4 w-4 text-zinc-400" /> {a.label}</div>
                <div className="mt-1 text-xs text-zinc-500">
                  {a.provider_label} · {a.mode === "live" ? <span className="text-emerald-300">Live</span> : <span className="text-amber-300">Test mode</span>}
                  {a.app_id_hint ? ` · App ID ${a.app_id_hint}` : ""}
                </div>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${a.keys_set ? "bg-emerald-500/10 text-emerald-300" : "bg-amber-500/10 text-amber-300"}`}>
                {a.keys_set ? "Keys saved" : "Keys missing"}
              </span>
            </div>
            <div className="mt-3 text-xs text-zinc-400">
              {a.gyms.length ? a.gyms.map((id) => data.gyms.find((g) => g.id === id)?.name).join(", ") : "No gyms yet"}
            </div>
            <div className="mt-3 flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(a)}>Edit</Button>
              <Button variant="outline" size="sm" onClick={() => remove(a)} disabled={a.gyms.length > 0} title={a.gyms.length ? "Move its gyms first" : ""}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
        {data && !data.accounts.length && (
          <div className="card-surface p-6 text-sm text-zinc-500 md:col-span-2 xl:col-span-3">
            No payment accounts yet. Add the shared Cashfree account for Bangalore and Hyderabad, and Stripe for Dubai.
          </div>
        )}
      </div>

      <div className="card-surface overflow-x-auto">
        {!data ? <div className="p-8 text-center text-sm text-zinc-500">Loading…</div> : (
          <table className="w-full text-sm" data-testid="payment-gyms">
            <thead>
              <tr className="border-b border-[#1f1f1f] text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                <th className="px-4 py-3">Gym</th>
                <th className="px-4 py-3">Currency</th>
                <th className="px-4 py-3">Renewals in the app</th>
                <th className="px-4 py-3" title="New Consult: the first payment is taken in FPA (QR or cash) before the consult can be marked sold">Consult payments in FPA</th>
              </tr>
            </thead>
            <tbody>
              {data.gyms.map((g) => {
                const acc = accountName(g.payment_account_id);
                return (
                  <tr key={g.id} className={`border-b border-[#161616] last:border-0 ${g.active ? "" : "opacity-50"}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-zinc-100">{g.name}</div>
                      <div className="text-[11px] text-zinc-500">{[g.city, g.country].filter(Boolean).join(", ")}</div>
                    </td>
                    <td className="px-4 py-3 text-zinc-400">{g.currency_code || "INR"}</td>
                    <td className="px-4 py-3">
                      <select className={selectCls} value={g.payment_account_id || DESK} onChange={(e) => assign(g, e.target.value)}
                        data-testid={`payment-assign-${g.id}`}>
                        <option value={DESK}>Request, pay at the front desk</option>
                        {data.accounts.map((a) => <option key={a.id} value={a.id}>{a.label} ({a.provider_label}{a.mode === "test" ? ", test" : ""})</option>)}
                      </select>
                      {acc && !acc.keys_set && <div className="mt-1 text-[11px] text-amber-300">Keys missing: members can only request</div>}
                    </td>
                    <td className="px-4 py-3">
                      <label className="inline-flex items-center gap-2 text-xs text-zinc-300">
                        <input type="checkbox" checked={!!g.consult_pay} data-testid={`consult-pay-${g.id}`}
                          onChange={async (e) => {
                            try { await setConsultPayGym(g.id, e.target.checked); toast.success(`${g.name}: consult payments ${e.target.checked ? "on" : "off"}`); load(); }
                            catch (er) { toast.error(er?.response?.data?.detail || "Couldn't change it"); }
                          }} />
                        {g.consult_pay ? "On" : "Off"}
                      </label>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing && <AccountDialog account={editing} providers={data?.providers || []}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function AccountDialog({ account, providers, onClose, onSaved }) {
  const isNew = !account.id;
  const [label, setLabel] = useState(account.label || "");
  const [provider, setProvider] = useState(account.provider || "cashfree");
  const [mode, setMode] = useState(account.mode || "test");
  const [keys, setKeys] = useState({});
  const [saving, setSaving] = useState(false);
  const p = providers.find((x) => x.key === provider) || { keys: [] };

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) await createPaymentAccount({ label, provider, mode, keys });
      else await updatePaymentAccount(account.id, { label, mode, keys });
      toast.success(`${label} saved`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-[#0f0f0f] border-[#262626] text-zinc-100" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Add payment account" : account.label}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Keys are stored securely and never shown again. Leave a key blank to keep the saved one.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Name">
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. LIFTR India (Cashfree)"
              className="bg-[#0b0b0b] border-[#262626] text-zinc-100" data-testid="payment-label" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Gateway">
              <select className={`${selectCls} w-full`} value={provider} onChange={(e) => setProvider(e.target.value)} disabled={!isNew}
                data-testid="payment-provider">
                {providers.map((x) => <option key={x.key} value={x.key}>{x.label}{x.currencies ? ` (${x.currencies.join(", ")})` : ""}</option>)}
              </select>
            </Field>
            <Field label="Mode">
              <select className={`${selectCls} w-full`} value={mode} onChange={(e) => setMode(e.target.value)} data-testid="payment-mode">
                <option value="test">Test (sandbox)</option>
                <option value="live">Live</option>
              </select>
            </Field>
          </div>
          {p.keys.map((k) => (
            <Field key={k} label={`${KEY_LABELS[k] || k}${mode === "live" ? " (live)" : " (test)"}`}>
              <Input type="password" autoComplete="off" value={keys[k] || ""} onChange={(e) => setKeys({ ...keys, [k]: e.target.value })}
                placeholder={isNew ? "" : "Saved. Paste a new one to replace it."}
                className="font-mono text-xs bg-[#0b0b0b] border-[#262626] text-zinc-100" data-testid={`payment-key-${k}`} />
            </Field>
          ))}
          {provider === "cashfree" && (
            <p className="text-[11px] text-zinc-500">Cashfree dashboard → Developers → API Keys. Add this server&apos;s address
              under Payment Gateway → Developers → Whitelisting if Cashfree asks for it.</p>
          )}
          {provider === "stripe" && (
            <p className="text-[11px] text-zinc-500">Stripe dashboard → Developers → API keys (secret key, sk_test_… or sk_live_…).</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || label.trim().length < 2} data-testid="payment-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs text-zinc-400">{label}</span>
      {children}
    </label>
  );
}
